import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { catchError, map, switchMap, filter, pluck, withLatestFrom, distinctUntilChanged, tap } from 'rxjs/operators';
import { Observable, of, merge } from 'rxjs';
import * as fromRouter from '~core/store';
import { ErrorResponse } from '~shared/models';
import * as fromBrandPLP from '../selectors';
import { BrandPLPService } from '~app/modules/brand-plp/services/brand-plp.service';
import { DEFAULT_LIMIT } from '~app/modules/generic/constants';
import {
  AppendProductsFailure,
  AppendProductsSuccess,
  LoadProducts,
  LoadProductsFailure,
  LoadProductsSuccess,
  ProductsActionTypes,
} from './products.actions';
import { BrandSearchResponse } from '~app/modules/brand-plp/models';
import { PriceTypeService } from '~shared/modules/price-type-toggler/services';
import { SortOption } from '~shared/modules/application-event/models/sort-option.model';
import { Params } from '@angular/router';
import { UrlUtils } from '~app/modules/generic/utils/url.utils';
import { RegionSelectorService } from '~shared/modules/region-selector/services';
import { getCountryDomainCode } from '~server/utils/country-detection.util';
import { CountryCode } from '~shared/constants';

@Injectable({
  providedIn: 'root',
})
export class ProductsEffects {
  selectedRegion: string;
  priceType$ = this.priceTypeSvc.priceType$;
  activeRoute$ = this.store.pipe(select(fromRouter.getRouterState));
  activeSort$ = this.store.pipe(select(fromBrandPLP.getSortOption));
  userRegion$ = this.regionSelectorService.userRegion$.pipe(
    filter((value) => value !== undefined),
    tap((value) => (this.selectedRegion = value))
  );
  @Effect()
  loadProducts$: Observable<Action> = this.actions$.pipe(
    ofType(ProductsActionTypes.LOAD_PRODUCTS),
    pluck('payload', 'queryOptions'),
    switchMap((queryOptions) =>
      this.brandPLPService.getProducts(queryOptions).pipe(
        map((response: BrandSearchResponse) => new LoadProductsSuccess({ response, queryOptions })),
        catchError((error: ErrorResponse) => of(new LoadProductsFailure({ error })))
      )
    )
  );

  @Effect()
  appendProducts$: Observable<Action> = this.actions$.pipe(
    ofType(ProductsActionTypes.APPEND_PRODUCTS),
    pluck('payload', 'queryOptions'),
    switchMap((queryOptions) =>
      this.brandPLPService.getProducts(queryOptions).pipe(
        map((response: BrandSearchResponse) => new AppendProductsSuccess(response)),
        catchError((error: ErrorResponse) => of(new AppendProductsFailure({ error })))
      )
    )
  );

  @Effect()
  updateItem$: Observable<Action> = merge(
    this.activeRoute$,
    this.activeSort$.pipe(distinctUntilChanged()),
    this.priceType$.pipe(distinctUntilChanged()),
    this.userRegion$
  ).pipe(
    withLatestFrom(this.activeRoute$, this.activeSort$.pipe(distinctUntilChanged())),
    filter(([routeOrPriceTypeOrCategoriesHubOrSort, routeState, sortOption]) => {
      if (!routeState || !routeState?.url) {
        return false;
      }

      return this.isValidSort(sortOption, routeState.queryParams, routeOrPriceTypeOrCategoriesHubOrSort as SortOption);
    }),
    map(([sortOption, routeState]) => {
      const { url, queryParams } = routeState;
      const brandSlug = UrlUtils.getSlugFromUrl(url);
      const categorySlug = UrlUtils.getCategoryFromUrl(url);

      return {
        queryParams: {
          ...queryParams,
          filter: {
            ...queryParams.filter,
            ...{
              categories: [categorySlug],
            },
            options: {
              ...queryParams?.filter?.options,
              ...{
                brand: [brandSlug],
              },
            },
          },
          ...{ region: this.selectedRegion },
        },
        sortOption,
      };
    }),
    switchMap(this.decideAppendOrLoad)
  );

  decideAppendOrLoad({ queryParams }) {
    let params = {
      ...queryParams,
    };
    if (!('limit' in params)) {
      params.limit = DEFAULT_LIMIT;
    }

    if (!('offset' in params)) {
      params.offset = 0;
    }

    return of(
      new LoadProducts({
        queryOptions: params,
      })
    );
  }

  constructor(
    private actions$: Actions,
    private brandPLPService: BrandPLPService,
    private store: Store<fromRouter.RouterState>,
    private priceTypeSvc: PriceTypeService,
    private readonly regionSelectorService: RegionSelectorService
  ) {}

  private isValidSort(currentSortOption: SortOption, queryParams: Params, sortOptionChange: SortOption): boolean {
    // don't get products unless the sort option is there ........ (wait for the default one to be set)
    if (!currentSortOption) {
      return false;
    }

    const validQueryParamOrderBy = queryParams && queryParams.orderBy && queryParams.orderBy !== 'custom';
    const isSortChange = sortOptionChange.code && sortOptionChange.key && sortOptionChange.value;
    if (isSortChange && !validQueryParamOrderBy) {
      return false;
    }

    if (
      validQueryParamOrderBy &&
      (queryParams.orderBy !== currentSortOption.key ||
        (queryParams.orderBy === currentSortOption.key && queryParams.order !== currentSortOption.value))
    ) {
      return false;
    }
    return true;
  }
}
